<form #f="ngForm" (ngSubmit)="requestDraft(f)" novalidate>
<nb-card class="dialog-card" [nbSpinner]="loading">
  <nb-card-header>
    <h4>{{ "pages.learnings.draft.title" | translate }}</h4>
  </nb-card-header>

  <!-- Step 1: the expert's note. The turn is shown so the note can refer to it. -->
  <ng-container *ngIf="!draft">
    <nb-card-body>
      <div class="turn">
        <div class="turn__row">
          <span class="label">{{ "pages.analytics.conversations.thread.learner" | translate }}</span>
          <p class="turn__text">{{ question }}</p>
        </div>
        <div class="turn__row">
          <span class="label">{{ "pages.analytics.conversations.thread.agent" | translate }}</span>
          <p class="turn__text turn__text--answer">{{ answer }}</p>
        </div>
        <div class="turn__row" *ngIf="rating || feedbackText">
          <span class="label">{{ "pages.learnings.draft.learnerFeedback" | translate }}</span>
          <p class="turn__text">
            <span *ngIf="rating === 'down'">👎</span><span *ngIf="rating === 'up'">👍</span>
            <span *ngIf="feedbackText"> „{{ feedbackText }}"</span>
          </p>
        </div>
      </div>

      <div class="form-field">
        <label class="label">
          {{ "pages.learnings.draft.expertNote" | translate }} *
          <nb-icon icon="question-mark-circle-outline" class="hint-icon"
            nbPopover="{{ 'pages.learnings.draft.expertNoteHint' | translate }}" nbPopoverTrigger="hover"></nb-icon>
        </label>
        <textarea
          nbInput
          fullWidth
          rows="5"
          name="expertNote"
          required
          minlength="3"
          maxlength="4000"
          [(ngModel)]="expertNote"
          [placeholder]="'pages.learnings.draft.expertNotePlaceholder' | translate"
        ></textarea>
        <span class="hint hint--warning" *ngIf="failed">{{ "pages.learnings.draft.failed" | translate }}</span>
      </div>
    </nb-card-body>

    <nb-card-footer>
      <button nbButton status="basic" size="small" type="button" (click)="close()">
        {{ "buttons.cancel" | translate }}
      </button>
      <button nbButton status="primary" size="small" type="submit" [disabled]="loading"
        [nbTooltip]="'pages.learnings.tooltips.propose' | translate">
        {{ "pages.learnings.draft.propose" | translate }}
      </button>
    </nb-card-footer>
  </ng-container>

  <!-- Step 2: the proposal, the learnings that already cover it, the scope. -->
  <ng-container *ngIf="draft as d">
    <nb-card-body>
      <!-- Not every correction is a learning. Say so first, but let the
           expert continue — they know the case, the model guesses. -->
      <div class="recommendation" *ngIf="d.proposal.recommendation !== 'learning'">
        <strong>{{ "pages.learnings.draft.recommendation." + d.proposal.recommendation + ".title" | translate }}</strong>
        <p>{{ "pages.learnings.draft.recommendation." + d.proposal.recommendation + ".text" | translate }}</p>
        <a
          *ngIf="d.proposal.recommendation === 'knowledge_base' && d.turn.bot_id"
          [routerLink]="['/tutors', d.turn.bot_id, 'knowledge-base']"
          (click)="close()"
        >
          {{ "pages.learnings.draft.openKnowledgeBase" | translate }}
        </a>
      </div>

      <div class="proposal">
        <span class="label">{{ "pages.learnings.draft.proposalLabel" | translate }}</span>
        <p class="proposal__rationale">{{ d.proposal.rationale }}</p>
        <p class="proposal__summary">
          <span class="badge">{{ "pages.learnings.types." + d.proposal.type | translate }}</span>
          <strong>{{ d.proposal.topic }}</strong>
        </p>
        <p class="proposal__content">{{ d.proposal.content }}</p>
      </div>

      <div class="form-field">
        <label class="label">
          {{ "pages.learnings.fields.analysisCategory" | translate }}
          <nb-icon
            icon="question-mark-circle-outline"
            class="hint-icon"
            nbPopover="{{ 'pages.learnings.hints.analysisCategory' | translate }}"
            nbPopoverTrigger="hover"
            nbPopoverPlacement="bottom"
          ></nb-icon>
        </label>
        <nb-select fullWidth size="small" [(selected)]="category">
          <nb-option *ngFor="let c of categories" [value]="c">
            {{ "pages.learnings.categories." + c | translate }}
          </nb-option>
        </nb-select>
      </div>

      <!-- What the agent ALREADY gets for this question — same search. -->
      <div class="similar" *ngIf="d.similar.length">
        <span class="label">{{ "pages.learnings.draft.similarTitle" | translate }}</span>
        <p class="hint" *ngIf="duplicate">{{ "pages.learnings.draft.duplicateHint" | translate }}</p>
        <ul>
          <li *ngFor="let s of d.similar" [class.is-duplicate]="s.learning_id === d.proposal.duplicate_of">
            <span class="badge" [class.badge--client]="s.scope === 'client'">
              {{
                (s.scope === "client" ? "pages.learnings.scope.clientWide" : "pages.learnings.scope.thisAgent")
                  | translate
              }}
            </span>
            <span class="similar__title">{{ s.topic || s.question }}</span>
            <span class="similar__score" *ngIf="s.score !== null && s.score !== undefined">{{ s.score | number: "1.2-2" }}</span>
            <span class="similar__content">{{ s.content }}</span>
          </li>
        </ul>
      </div>

      <div class="form-field">
        <label class="label">
          {{ "pages.learnings.draft.scopeLabel" | translate }}
          <nb-icon icon="question-mark-circle-outline" class="hint-icon"
            nbPopover="{{ 'pages.learnings.tooltips.scope' | translate }}" nbPopoverTrigger="hover"></nb-icon>
        </label>
        <nb-radio-group [(ngModel)]="scope" [ngModelOptions]="{ standalone: true }">
          <nb-radio value="bot">{{ "pages.learnings.draft.scopeBot" | translate: { agent: d.turn.bot_name || "" } }}</nb-radio>
          <nb-radio value="client" [disabled]="!canWriteClient">
            {{ "pages.learnings.scope.clientWide" | translate }}
          </nb-radio>
        </nb-radio-group>
        <span class="hint">{{ d.proposal.scope_reason }}</span>
        <span class="hint" *ngIf="!canWriteClient">{{ "pages.learnings.draft.clientScopeNeedsRole" | translate }}</span>
      </div>

      <nb-checkbox
        *ngIf="d.turn.course_id"
        [nbTooltip]="'pages.learnings.tooltips.courseOnly' | translate"
        [(ngModel)]="courseOnly"
        [ngModelOptions]="{ standalone: true }"
      >
        {{ "pages.learnings.draft.courseOnly" | translate: { course: d.turn.course_name || "" } }}
      </nb-checkbox>
    </nb-card-body>

    <nb-card-footer>
      <button nbButton status="basic" size="small" type="button" (click)="back()"
        [nbTooltip]="'pages.learnings.tooltips.back' | translate">
        {{ "pages.learnings.draft.back" | translate }}
      </button>
      <button nbButton status="primary" size="small" type="button" (click)="proceed()"
        [nbTooltip]="'pages.learnings.tooltips.proceed' | translate">
        {{ "pages.learnings.draft.proceed" | translate }}
      </button>
    </nb-card-footer>
  </ng-container>
</nb-card>
</form>
